import classNames from 'classnames';
import * as React from 'react';
import type { ReactNode } from 'react';
import { Link } from '../Link/Link';
import { List } from '../List/List';
import styles from './StudySubjects.module.css';

export interface StudySubject {
    name: string;
    url: string;
    code?: string;
}

export interface Specialization {
    name: string;
    subjects: StudySubject[];
}

export interface ProgramSemesterSubjects {
    subjects: StudySubject[];
    specializations: Specialization[];
}

export interface StudySubjectsProps {
    programSemesters: ProgramSemesterSubjects[];
    standalone?: boolean;
}

export const StudySubjects: React.FC<StudySubjectsProps> = props => {
    const { programSemesters, standalone = true } = props;

    const renderLabel = (name: string, code?: string) => (code ? `${name} (${code})` : name);

    const renderSubjects = (subjects: StudySubject[]): ReactNode => (
        <List
            items={subjects}
            className={styles.SubjectList}
            renderItem={(subject, index) => (
                <li className={styles.Subject} key={`studySubject__${index}`}>
                    {subject.url ? (
                        <Link href={subject.url} label={renderLabel(subject.name, subject.code)} />
                    ) : (
                        <span>
                            {subject.name} ({subject.code})
                        </span>
                    )}
                </li>
            )}
        />
    );
    const renderSpecializations = (specializations: Specialization[]): ReactNode =>
        specializations.length > 0 &&
        specializations.map(spec => (
            <React.Fragment key={spec.name}>
                <h4 className={styles.SpecializationHeading}>{spec.name}</h4>
                {renderSubjects(spec.subjects)}
            </React.Fragment>
        ));

    return (
        <div className={classNames(styles.StudySubjects, standalone && styles['Variant--standalone'])}>
            {programSemesters.length > 0 && (
                <List
                    items={programSemesters}
                    className={styles.Semesters}
                    renderItem={(semester, index) => (
                        <li key={`studySemester__${index}`}>
                            <h3 className={styles.SemesterHeading}>{`${index + 1}. semester`}</h3>
                            {semester.subjects.length > 0 && renderSubjects(semester.subjects)}
                            {semester.specializations.length > 0 && renderSpecializations(semester.specializations)}
                        </li>
                    )}
                />
            )}
        </div>
    );
};
